import { ReactElement, SyntheticEvent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import cc from 'classcat';
import { EIconSize, ICON_SIZE } from 'utils/constants/app/ui';
import SpringConfigs from 'utils/constants/swarm/spring-configs';
import { getTime } from 'utils/date-manipulation/get-tournament-time';
import { getMountPath, mountPoint } from 'utils/generic/route-manipulation';
import { isMobile } from 'utils/is-mobile';
import { ETournamentProductTypeIds } from 'interfaces/casino-data';
import { ISportTournament } from 'interfaces/sport-tournaments';
import { TTabDataKey } from 'interfaces/sportsbook';
import { getTournaments } from 'services/casino/casino-tournaments';
import { EventCollapse } from 'components/EventCollapse';
import { GlobalIcon } from 'components/GlobalIcon';
import { Loading } from 'components/Loading';
import { addQuery } from 'components/QueryRoute';
import { useSportsbookConfigs } from 'hooks/sportsbook/useSportsbookConfigs';
import { useCurrency } from 'hooks/useCurrency';
import { useSbRouteMatch } from 'newhooks/generic/useSbRouteMatch';
import { setTournamentSportIds } from 'store/actions';
import {
  getSportsbookSelectedSport,
  getSportsData
} from 'store/selectors/new-sportsbook';
import { getTournamentSportIds } from 'store/selectors/sport-data';
import './index.less';

if (SpringConfigs.IS_RTL) {
  import('./rtl.less');
}

export const SportTournament = (): ReactElement => {
  const sportsbookConfigs = useSportsbookConfigs();

  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const [sportTournaments, setSportTournaments] = useState<
    null | ISportTournament[]
  >(null);

  const [selectedTournament, setSelectedTournament] =
    useState<null | ISportTournament>(null);

  const sports = useSelector(getSportsData)?.[sportsbookConfigs.type];
  const selectedSport = useSelector(getSportsbookSelectedSport);
  const tournamentSportIds = useSelector(getTournamentSportIds);

  const history = useHistory();
  const dispatch = useDispatch();

  const { t } = useTranslation();
  const { path } = useSbRouteMatch();
  const { currency } = useCurrency();

  const handleSelectTournament = (tournament?: ISportTournament) => {
    if (tournament?.SportRuleList?.SelectedSportItems) {
      const ids: number[] = [];

      tournament.SportRuleList.SelectedSportItems.map(sport => {
        ids.push(sport.Id);
      });

      dispatch(setTournamentSportIds(ids));
      setSelectedTournament(tournament);

      if (!ids.includes(selectedSport?.id as number)) {
        const currentSport = sports?.find(({ id }) => ids.includes(Number(id)));

        history.push(
          `${mountPoint(window.getPathname())}/${currentSport?.alias}`
        );
      }
    }
  };

  const handleResetSelectedTournament = () => {
    dispatch(setTournamentSportIds(null));
    setSelectedTournament(null);
  };

  const handleRedirect = (event: SyntheticEvent) => {
    const redirectUrl = `${getMountPath(path)}/${
      TTabDataKey.TOURNAMENTS
    }/?tournamentId=${selectedTournament?.Id}`;

    event.preventDefault();
    event.stopPropagation();
    history.push(
      isMobile()
        ? addQuery({ tournamentId: selectedTournament?.Id.toString() })
        : redirectUrl
    );
  };

  useEffect(() => {
    if (isOpen) {
      getTournaments(
        {
          product_type_id: ETournamentProductTypeIds.SPORT
        },
        (data: { result?: Array<ISportTournament> }) => {
          if (data?.result) {
            setSportTournaments(data.result);
            setIsLoading(false);
          }
        }
      );
    }
  }, [isOpen]);

  useEffect(() => {
    if (selectedTournament && !tournamentSportIds?.length) {
      handleResetSelectedTournament();
    }
  }, [selectedTournament, tournamentSportIds?.length]);

  return (
    <div
      className={cc([
        'sport__tournament',
        { 'sport__tournament--mobile': isMobile() }
      ])}
    >
      <EventCollapse
        title={
          <span className="sport__tournament--mobile__title">
            {t('pages.tournaments')}
          </span>
        }
        noMargin={isMobile()}
        icon={
          <GlobalIcon
            lib="generic"
            name="tournament"
            theme="colored"
            className="text-color"
            size={isMobile() ? ICON_SIZE.MOBILE_20 : ICON_SIZE.DESKTOP_24}
          />
        }
        onChange={(value: boolean) => {
          setIsOpen(value);
        }}
        defaultActive={false}
        headerClassName={isMobile() ? 'sport__tournament--mobile__header' : ''}
        key="1-sport-tournament"
        size="middle"
      >
        <div className="sport__tournament__wrapper">
          {isLoading ? (
            <div className="sport__tournament-loading">
              <Loading size={EIconSize._16} />
            </div>
          ) : sportTournaments ? (
            <div
              className={selectedTournament ? 'selected__tour-container' : ''}
            >
              {selectedTournament ? (
                <div className="sports__tour-selected">
                  <div className="sports__tour-header">
                    <span className="sports__tour-name">
                      {selectedTournament.Name}
                    </span>
                    <span
                      className="sports__tour-reset"
                      onClick={handleResetSelectedTournament}
                    >
                      <span>{t('sportsbook.reset')}</span>
                      <GlobalIcon
                        lib="generic"
                        name="close"
                        theme="default"
                        size={EIconSize._8}
                      />
                    </span>
                  </div>
                  <div className="sports__tour-info">
                    <span>{t('casino.buyInFee')}: </span>
                    <span>
                      {' '}
                      {currency}{' '}
                      {selectedTournament?.RegistrationAmount?.toString()}
                    </span>
                  </div>
                  <div className="sports__tour-info">
                    {getTime(selectedTournament.StartDate, '.')} -{' '}
                    {getTime(selectedTournament.EndDate, '.')}
                  </div>
                  <div className="sports__tour-details-container">
                    <span>
                      {currency} {selectedTournament.PrizeFund}
                    </span>
                    <span
                      onClick={handleRedirect}
                      className="sports__tour-see-details"
                    >
                      {t('sportsbook.seeDetails')}
                    </span>
                  </div>
                </div>
              ) : sportTournaments.length ? (
                sportTournaments.map((item, index) => (
                  <div
                    className="sport__tour-item"
                    key={index}
                    onClick={() => handleSelectTournament(item)}
                  >
                    {item.Name}
                  </div>
                ))
              ) : (
                <div className="sports__tour-empty">
                  {t('emptyMessages.noTournamentGame')}
                </div>
              )}
            </div>
          ) : (
            <div className="sports__tour-empty">
              {t('emptyMessages.noTournamentGame')}
            </div>
          )}
        </div>
      </EventCollapse>
    </div>
  );
};
